"use client"

import type { ReactNode } from "react"
import { useCountry } from "@/components/layout/country-provider"
import { BookingCta } from "@/components/ui/booking-cta"
import { ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import { PRODUCT_IDS } from "@/lib/booking"
import { cn } from "@/lib/utils"

/**
 * The result-panel CTA shared by every calculator under /tools: it books the
 * market's blood check-up directly (booking.aheadhealth.com, blood-only tab)
 * rather than handing the reader to the quiz first.
 *
 * DE deliberately omits the product id, like Blood Test Card: it books the
 * Vorsorge-Starter, not the Advanced blood test, and bookingUrlForCountry's
 * generic `?step=product&country=de` entry is what lands there. CH and NL
 * preselect the Advanced blood test.
 */
export function CalculatorBookingCta({
  calculator,
  children,
}: {
  /** Short calculator id (`bmi`, `fib4`…): test id and tracking label. */
  calculator: string
  children: ReactNode
}) {
  const { code: country } = useCountry()
  // country-gate-ignore: which blood product the market books, not access control
  const isDe = country === "de"
  return (
    <BookingCta
      {...(isDe ? {} : { productId: PRODUCT_IDS.bloodTest })}
      category="blood"
      testId={`${calculator}-booking-cta`}
      trackingLabel={`Book check-up from ${calculator} calculator`}
      trackingLocation={`${calculator}-calculator-result`}
      className={cn(buttonVariants({ variant: "primary" }), "group mt-4")}
    >
      {children}
      <ArrowRightIcon className="ml-2 transition-transform duration-200 ease-out group-hover:translate-x-1" />
    </BookingCta>
  )
}
